import { useState, useEffect, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  type User,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import { writeUserProfile } from "../services/firestore";

const ALLOWED_EMAILS = [
  'gabriellc2307@gmail.com',
  'sarahlourenco.rainhadosanjos@gmail.com',
];

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !ALLOWED_EMAILS.includes(user.email ?? '')) {
        await firebaseSignOut(auth);
        setState({ user: null, loading: false, error: 'unauthorized' });
        return;
      }
      if (user) {
        writeUserProfile(user.uid, user.email ?? '', user.displayName).catch(() => {});
      }
      setState({ user, loading: false, error: null });
    });
    return unsubscribe;
  }, []);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        return result.user;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Erro ao fazer login";
        setState((prev) => ({ ...prev, loading: false, error: message }));
        throw error;
      }
    },
    [],
  );

  const signUpWithEmail = useCallback(
    async (email: string, password: string, displayName?: string) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        if (displayName) {
          await updateProfile(result.user, { displayName });
        }
        return result.user;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Erro ao criar conta";
        setState((prev) => ({ ...prev, loading: false, error: message }));
        throw error;
      }
    },
    [],
  );

  const signInWithGoogle = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erro ao fazer login com Google";
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao fazer logout";
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erro ao enviar email de recuperação";
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    resetPassword,
    clearError,
  };
}
